import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Heart, Loader2, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";

const TR = {
  sk: {
    title: "Podpor výstavbu centra",
    hofOptIn: "Zobraziť moje meno v Sieni slávy (bez sumy)",
    subtitle: "Príspevok na Tréningové centrum — od €15",
    body: <>Podpor výstavbu <strong className="text-gold-400">Tréningového centra SauberEko</strong> pre orbitálnych zváračov, ručných zváračov a potrubárov. Každé euro ide na vybavenie a priestory, kde budeme školiť novú generáciu majstrov. Tvoje meno sa ako poďakovanie objaví v <strong className="text-gold-400">Sieni slávy</strong> v sekcii Podpora Tréningové centrum SauberEko.</>,
    verifying: "Overujem platbu, sekundu...",
    thanksHead: "Ďakujeme! Prijatých",
    thanksSub: "Poďakovanie ti príde na email. Admin sa ti určite odvďačí.",
    errorVerify: "⚠️ Stripe potvrdil platbu, ale server ju nestihol overiť. PROSÍM NESKÚŠAJ ZNOVA — peniaze sú v poriadku. Kontaktuj nás na info@saubereko.eu so screenshotom z banky a my to vyriešime.",
    sumLabel: "Suma (€)",
    sumHint: "EUR · min €15",
    messageLabel: "Tvoj odkaz / nápad (voliteľné, max 1500 znakov)",
    messagePlaceholder: "Napríklad: ‚Pridajte prosím Orbitec OW programy.‘ alebo ‚Tigik zachránil mi týždeň práce.‘",
    cta: "Podporiť",
    secure: "Bezpečná platba cez Stripe. Po platbe ti pošleme faktúru aj certifikát-email.",
    bonusToast: (a, days) => `Veľká vďaka! ${a.toFixed(2)} € prijaté — certifikát ide na email.${days > 0 ? ` Pridali sme ti +${days} dní ACTIVE ako poďakovanie!` : ""}`,
    cancelToast: "Platba zrušená — nič strašné, môžeš to skúsiť znova.",
    invalidAmount: "Zadaj sumu od €15 do €5000.",
    openFailed: "Nepodarilo sa otvoriť platobnú bránu.",
  },
  en: {
    title: "Support building the centre",
    hofOptIn: "Show my name on the Wall of Fame (no amount)",
    subtitle: "Contribution to the Training Centre — from €15",
    body: <>Support building the <strong className="text-gold-400">SauberEko Training Centre</strong> for orbital welders, manual welders and pipefitters. Every euro goes into equipment and premises where we will train the next generation of masters. As a thank-you, your name will appear on the <strong className="text-gold-400">Wall of Fame</strong> under SauberEko Training Centre supporters.</>,
    verifying: "Verifying payment, one moment...",
    thanksHead: "Thank you! Received",
    thanksSub: "A thank-you email is on its way. Admin will reward you.",
    errorVerify: "⚠️ Stripe confirmed the payment but the server couldn't verify it. PLEASE DO NOT RETRY — your money is safe. Contact us at info@saubereko.eu with your bank statement screenshot and we'll resolve it.",
    sumLabel: "Amount (€)",
    sumHint: "EUR · min €15",
    messageLabel: "Your message / idea (optional, max 1500 chars)",
    messagePlaceholder: "E.g. 'Please add Orbitec OW programs.' or 'Tigik saved me a week of work.'",
    cta: "Support",
    secure: "Secure payment via Stripe. We'll email you an invoice and a thank-you certificate.",
    bonusToast: (a, days) => `Huge thanks! ${a.toFixed(2)} € received — certificate on its way.${days > 0 ? ` We added +${days} bonus ACTIVE days as a thank you!` : ""}`,
    cancelToast: "Payment cancelled — no worries, you can try again.",
    invalidAmount: "Enter an amount between €15 and €5000.",
    openFailed: "Could not open the payment gateway.",
  },
  cs: {
    title: "Podpoř výstavbu centra",
    hofOptIn: "Zobrazit mé jméno v Síni slávy (bez částky)",
    subtitle: "Příspěvek na Tréninkové centrum — od €15",
    body: <>Podpoř výstavbu <strong className="text-gold-400">Tréninkového centra SauberEko</strong> pro orbitální svářeče, ruční svářeče a potrubáře. Každé euro jde na vybavení a prostory, kde budeme školit novou generaci mistrů. Jako poděkování se tvé jméno objeví v <strong className="text-gold-400">Síni slávy</strong> v sekci Podpora Tréninkové centrum SauberEko.</>,
    verifying: "Ověřuji platbu, sekundu...",
    thanksHead: "Děkujeme! Přijato",
    thanksSub: "Poděkování ti přijde na email. Admin se ti určitě odvděčí.",
    errorVerify: "Platbu se nepodařilo ověřit — kontaktuj nás na info@saubereko.eu.",
    sumLabel: "Částka (€)",
    sumHint: "EUR · min €15",
    messageLabel: "Tvůj vzkaz / nápad (volitelné, max 1500 znaků)",
    messagePlaceholder: "Například: ‚Přidejte prosím Orbitec OW programy.‘ nebo ‚Tigik mi zachránil týden práce.‘",
    cta: "Podpořit",
    secure: "Bezpečná platba přes Stripe. Po platbě ti pošleme fakturu i certifikát-email.",
    bonusToast: (a, days) => `Velká díky! ${a.toFixed(2)} € přijato — certifikát jde na email.${days > 0 ? ` Přidali jsme ti +${days} dní ACTIVE jako poděkování!` : ""}`,
    cancelToast: "Platba zrušena — nic hrozného, můžeš to zkusit znovu.",
    invalidAmount: "Zadej částku od €15 do €5000.",
    openFailed: "Nepodařilo se otevřít platební bránu.",
  },
  de: {
    title: "Bau des Zentrums unterstützen",
    hofOptIn: "Meinen Namen in der Ruhmeshalle zeigen (ohne Betrag)",
    subtitle: "Beitrag zum Trainingszentrum — ab €15",
    body: <>Unterstütze den Bau des <strong className="text-gold-400">SauberEko Trainingszentrums</strong> für Orbital-, Hand-Schweißer und Rohrleitungsbauer. Jeder Euro fließt in Ausstattung und Räume, in denen wir die nächste Generation von Meistern ausbilden. Als Dank erscheint dein Name in der <strong className="text-gold-400">Ruhmeshalle</strong> unter den Unterstützern des SauberEko Trainingszentrums.</>,
    verifying: "Zahlung wird überprüft, einen Moment...",
    thanksHead: "Danke! Erhalten",
    thanksSub: "Eine Dankes-E-Mail ist unterwegs. Der Admin wird sich revanchieren.",
    errorVerify: "⚠️ Stripe hat die Zahlung bestätigt, aber der Server konnte sie nicht verifizieren. BITTE NICHT ERNEUT VERSUCHEN — dein Geld ist sicher. Kontaktiere uns unter info@saubereko.eu mit einem Screenshot deines Bankauszugs, wir lösen es.",
    sumLabel: "Betrag (€)",
    sumHint: "EUR · min €15",
    messageLabel: "Deine Nachricht / Idee (optional, max 1500 Zeichen)",
    messagePlaceholder: "Z. B. ‚Bitte Orbitec OW Programme hinzufügen.‘ oder ‚Tigik hat mir eine Woche Arbeit gespart.‘",
    cta: "Unterstützen",
    secure: "Sichere Zahlung über Stripe. Nach der Zahlung erhältst du Rechnung und Dankes-Zertifikat per E-Mail.",
    bonusToast: (a, days) => `Vielen Dank! ${a.toFixed(2)} € erhalten — Zertifikat unterwegs.${days > 0 ? ` Wir haben dir +${days} ACTIVE-Bonustage als Dank hinzugefügt!` : ""}`,
    cancelToast: "Zahlung abgebrochen — kein Problem, du kannst es erneut versuchen.",
    invalidAmount: "Bitte einen Betrag zwischen €15 und €5000 eingeben.",
    openFailed: "Zahlungsgateway konnte nicht geöffnet werden.",
  },
  pl: {
    title: "Wesprzyj budowę centrum",
    hofOptIn: "Pokaż moje imię w Galerii Sław (bez kwoty)",
    subtitle: "Wkład w Centrum Szkoleniowe — od €15",
    body: <>Wesprzyj budowę <strong className="text-gold-400">Centrum Szkoleniowego SauberEko</strong> dla spawaczy orbitalnych, ręcznych i monterów rurociągów. Każde euro idzie na wyposażenie i pomieszczenia, w których wyszkolimy nowe pokolenie mistrzów. W podziękowaniu twoje imię pojawi się w <strong className="text-gold-400">Galerii Sław</strong> w sekcji Wsparcie Centrum Szkoleniowe SauberEko.</>,
    verifying: "Weryfikuję płatność, chwila...",
    thanksHead: "Dziękujemy! Otrzymano",
    thanksSub: "Email z podziękowaniem jest w drodze. Admin ci się odwdzięczy.",
    errorVerify: "⚠️ Stripe potwierdził płatność, ale serwer nie zdążył jej zweryfikować. PROSZĘ NIE PRÓBOWAĆ PONOWNIE — twoje pieniądze są bezpieczne. Skontaktuj się z nami na info@saubereko.eu ze screenshotem z banku, rozwiążemy to.",
    sumLabel: "Kwota (€)",
    sumHint: "EUR · min €15",
    messageLabel: "Twoja wiadomość / pomysł (opcjonalne, max 1500 znaków)",
    messagePlaceholder: "Np. ‚Dodajcie proszę programy Orbitec OW.‘ albo ‚Tigik uratował mi tydzień pracy.‘",
    cta: "Wesprzyj",
    secure: "Bezpieczna płatność przez Stripe. Po płatności otrzymasz fakturę i certyfikat-email.",
    bonusToast: (a, days) => `Wielkie dzięki! ${a.toFixed(2)} € otrzymane — certyfikat w drodze.${days > 0 ? ` Dodaliśmy ci +${days} dni ACTIVE w ramach podziękowania!` : ""}`,
    cancelToast: "Płatność anulowana — nic strasznego, możesz spróbować ponownie.",
    invalidAmount: "Wprowadź kwotę od €15 do €5000.",
    openFailed: "Nie udało się otworzyć bramki płatności.",
  },
  fr: {
    title: "Soutiens la construction du centre",
    hofOptIn: "Afficher mon nom au Mur de la gloire (sans montant)",
    subtitle: "Contribution au Centre de formation — à partir de 15 €",
    body: <>Soutiens la construction du <strong className="text-gold-400">Centre de formation SauberEko</strong> pour soudeurs orbitaux, soudeurs manuels et tuyauteurs. Chaque euro finance l'équipement et les locaux où nous formerons la prochaine génération de pros. En remerciement, ton nom apparaîtra au <strong className="text-gold-400">Mur de la gloire</strong> parmi les soutiens du Centre de formation SauberEko.</>,
    verifying: "Vérification du paiement, une seconde...",
    thanksHead: "Merci ! Reçu",
    thanksSub: "Un merci te parviendra par email. L'admin te remerciera personnellement.",
    errorVerify: "⚠️ Stripe a confirmé le paiement, mais le serveur n'a pas pu le vérifier à temps. MERCI DE NE PAS RÉESSAYER — l'argent est bien passé. Contacte-nous à info@saubereko.eu avec un screenshot de ta banque et on réglera ça.",
    sumLabel: "Montant (€)",
    sumHint: "EUR · min 15 €",
    messageLabel: "Ton message / idée (facultatif, max 1500 caractères)",
    messagePlaceholder: "Par exemple : « Ajoutez s'il vous plaît les programmes Orbitec OW. » ou « Tigik m'a sauvé une semaine de boulot. »",
    cta: "Soutenir",
    secure: "Paiement sécurisé via Stripe. Après paiement, on t'envoie facture et certificat par email.",
    bonusToast: (a, days) => `Merci énorme ! ${a.toFixed(2)} € reçu — certificat en route par email.${days > 0 ? ` On t'a ajouté +${days} jours ACTIVE en remerciement !` : ""}`,
    cancelToast: "Paiement annulé — pas grave, tu peux réessayer quand tu veux.",
    invalidAmount: "Saisis un montant entre 15 € et 5000 €.",
    openFailed: "Impossible d'ouvrir la passerelle de paiement.",
  },
};

/**
 * SupportTigik — donation card in Profile.
 * - Custom amount input (min €15, max €5000)
 * - Optional message ("what would you improve?")
 * - Creates Stripe Checkout, redirects to Stripe
 * - After return (?donation=success&session_id=...), verifies & shows thanks
 */
export default function SupportTigik() {
  const { lang } = useLang();
  const tr = TR[lang] || TR.sk;
  const [params, setParams] = useSearchParams();
  const [amount, setAmount] = useState("15");
  const [message, setMessage] = useState("");
  const [showInHof, setShowInHof] = useState(true);
  const [busy, setBusy] = useState(false);
  const [postPayState, setPostPayState] = useState(null);
  const [paidAmount, setPaidAmount] = useState(null);
  const [bonusDays, setBonusDays] = useState(0);

  useEffect(() => {
    if (window.location.hash === "#support") {
      setTimeout(() => document.getElementById("support")?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
  }, []);

  useEffect(() => {
    const status = params.get("donation");
    const sid = params.get("session_id");
    if (!status) return;
    if (status === "success" && sid) {
      setPostPayState("verifying");
      let tries = 0;
      const MAX_TRIES = 30; // 30 × 2s = 60s — covers 3D Secure / SCA flows
      const poll = async () => {
        tries += 1;
        try {
          const r = await api.get(`/donations/check-payment/${sid}`);
          if (r.data.paid) {
            setPostPayState("paid");
            setPaidAmount(r.data.amount);
            setBonusDays(r.data.bonus_days || 0);
            toast.success(tr.bonusToast(r.data.amount, r.data.bonus_days || 0));
            setTimeout(() => {
              params.delete("donation");
              params.delete("session_id");
              setParams(params, { replace: true });
            }, 1200);
            return;
          }
          // Not paid yet — keep polling. Stripe 3DS / SCA can take a while.
          if (tries >= MAX_TRIES) { setPostPayState("error"); return; }
          setTimeout(poll, 2000);
        } catch {
          // Network/server error — keep polling unless we hit the cap. The
          // server-side handler now returns 200 with paid=false on Stripe API
          // failures, so true exceptions are rare.
          if (tries >= MAX_TRIES) { setPostPayState("error"); return; }
          setTimeout(poll, 2000);
        }
      };
      poll();
    } else if (status === "cancel") {
      setPostPayState("cancel");
      toast.info(tr.cancelToast);
      setTimeout(() => {
        params.delete("donation");
        setParams(params, { replace: true });
        setPostPayState(null);
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const donate = async (e) => {
    e?.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt < 15 || amt > 5000) {
      toast.error(tr.invalidAmount);
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/donations/checkout", {
        amount: amt,
        message: message.trim() || null,
        show_in_hof: showInHof,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e2) {
      toast.error(e2.response?.data?.detail || tr.openFailed);
      setBusy(false);
    }
  };

  return (
    <div id="support" data-testid="support-tigik" className="border border-gold-700/30 bg-gradient-to-br from-zinc-900 to-black p-6 mb-6 relative overflow-hidden">
      <div className="absolute -top-12 -right-12 w-40 h-40 bg-gold-500/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-amber-500/5 blur-3xl pointer-events-none" />

      <div className="relative">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-gold-500/20 border border-gold-600 flex items-center justify-center">
            <Heart className="w-5 h-5 text-gold-400" fill="currentColor" />
          </div>
          <div>
            <div className="font-display font-black text-lg flex items-center gap-2">
              {tr.title}
              <Sparkles className="w-4 h-4 text-gold-500" />
            </div>
            <div className="text-xs text-zinc-500">{tr.subtitle}</div>
          </div>
        </div>

        <p className="text-sm text-zinc-400 leading-relaxed my-4">{tr.body}</p>

        {postPayState === "verifying" && (
          <div className="border border-amber-700/40 bg-amber-950/20 p-4 mb-4 inline-flex items-center gap-3 text-sm text-amber-300" data-testid="donation-verifying">
            <Loader2 className="w-4 h-4 animate-spin" /> {tr.verifying}
          </div>
        )}
        {postPayState === "paid" && (
          <div className="border border-green-700/40 bg-green-950/20 p-4 mb-4 text-sm text-green-300" data-testid="donation-paid">
            <div className="inline-flex items-center gap-2 font-display font-bold">
              <CheckCircle2 className="w-4 h-4" /> {tr.thanksHead} {paidAmount?.toFixed(2)} €.
            </div>
            <div className="text-xs text-zinc-400 mt-1">{tr.thanksSub}</div>
          </div>
        )}
        {postPayState === "error" && (
          <div className="border-2 border-red-600 bg-red-950/40 p-5 mb-4 text-sm text-red-200" data-testid="donation-error">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
              <div>{tr.errorVerify}</div>
            </div>
          </div>
        )}

        {(postPayState === "paid" || postPayState === "error") ? null : (
        <form onSubmit={donate} className="space-y-3">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">{tr.sumLabel}</label>
            <div className="flex items-center gap-2">
              <input
                type="number" min="15" max="5000" step="0.5"
                value={amount} onChange={(e) => setAmount(e.target.value)}
                data-testid="donation-amount-input" placeholder="15"
                className="w-40 bg-black border border-zinc-700 px-3 py-2 text-lg font-display font-bold text-white focus:border-gold-600 outline-none"
              />
              <span className="text-zinc-500 text-sm">{tr.sumHint}</span>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">{tr.messageLabel}</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 1500))}
              data-testid="donation-message-input"
              rows={3}
              placeholder={tr.messagePlaceholder}
              className="w-full bg-black border border-zinc-700 px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-gold-600 outline-none resize-none"
            />
            <div className="text-[10px] text-zinc-600 mt-1">{message.length}/1500</div>
          </div>

          <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer select-none">
            <input
              type="checkbox" checked={showInHof} onChange={(e) => setShowInHof(e.target.checked)}
              data-testid="donation-show-in-hof-checkbox" className="accent-yellow-500 w-4 h-4"
            />
            {tr.hofOptIn}
          </label>

          <button
            type="submit" disabled={busy} data-testid="donation-submit-btn"
            className="wb-btn-primary px-6 py-3 text-sm inline-flex items-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
            {tr.cta} {parseFloat(amount) > 0 ? `${parseFloat(amount).toFixed(2)} €` : ""}
          </button>
          <div className="text-[10px] font-mono text-zinc-600">{tr.secure}</div>
        </form>
        )}
      </div>
    </div>
  );
}
